import type { IntakeSpec, MechanicalBracketDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { HOLE_DEFAULTS } from '@/lib/intakeBuilderDefaults';
import { METRIC_SCREWS } from '@shared/hardwareCatalog';

function getDetails(intake: IntakeSpec): MechanicalBracketDetails {
  return intake.details as MechanicalBracketDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const dims = intake.core.size ?? { length: 100, width: 60, height: 4 };
  const plateThickness = dims.height ?? 4;

  if (d.bracketType === 'L' || d.bracketType === 'angle') {
    return [
      {
        id: 'plate_horizontal', name: '水平板', kind: 'panel', materialId: 'mat-primary',
        width: dims.length ?? 100, depth: dims.width ?? 60, thickness: plateThickness,
        position: [0, 0, 0], rotation: [0, 0, 0], tags: ['plate'], paramRefs: [], cutouts: [],
      },
      {
        id: 'plate_vertical', name: '垂直板', kind: 'panel', materialId: 'mat-primary',
        width: dims.length ?? 100, depth: dims.width ?? 60, thickness: plateThickness,
        position: [0, -((dims.width ?? 60) / 2), (dims.width ?? 60) / 2],
        rotation: [90, 0, 0], tags: ['plate'], paramRefs: [], cutouts: [],
      },
    ];
  }
  return [
    {
      id: 'plate', name: 'プレート', kind: 'panel', materialId: 'mat-primary',
      width: dims.length ?? 100, depth: dims.width ?? 60, thickness: plateThickness,
      position: [0, 0, 0], rotation: [0, 0, 0], tags: ['plate'], paramRefs: [], cutouts: [],
    },
  ];
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const hole = HOLE_DEFAULTS[d.holeSize];
  const out: Feature[] = [];
  const platePartId =
    d.bracketType === 'L' || d.bracketType === 'angle' ? 'plate_horizontal' : 'plate';

  const dims = intake.core.size ?? { length: 100, width: 60, height: 4 };
  const usable = (dims.length ?? 100) - 2 * (hole.nominal * hole.edgeOffsetMul);
  const spacing = d.holeCount > 1 ? usable / (d.holeCount - 1) : 0;

  // threaded_hole feature is B2 rest; for now we keep a screw_hole + thread:true.
  const isThreaded = d.holeFinish === 'threaded'; // TODO(B2-rest): use threaded_hole feature
  // METRIC_SCREWS only covers M2..M8; M10 brackets get screw_hole only (no
  // counterbore/countersink callouts).
  const screwSpec = (d.holeSize === 'M10' ? undefined : METRIC_SCREWS[d.holeSize as 'M3' | 'M4' | 'M5' | 'M6' | 'M8']);

  for (let i = 0; i < d.holeCount; i++) {
    const x = -((dims.length ?? 100) / 2) + hole.nominal * hole.edgeOffsetMul + spacing * i;
    const sh_id = `sh-${i + 1}`;
    out.push({
      id: sh_id,
      partId: platePartId,
      kind: 'screw_hole',
      size: d.holeSize,
      length: dims.height ?? 4,
      head:
        d.holeFinish === 'countersunk'
          ? 'flat'
          : d.holeFinish === 'counterbored'
            ? 'socket'
            : 'pan',
      axis: 'z',
      thread: isThreaded,
      holeOversize: hole.throughOversize,
      teardrop: false,
      position: [x, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });

    if (d.holeFinish === 'counterbored' && screwSpec) {
      out.push({
        id: `cb-${i + 1}`,
        partId: platePartId,
        kind: 'counterbore',
        diameter: screwSpec.socketHeadDiameter + 0.5,
        depth: screwSpec.recommendedCounterboreDepth,
        position: [x, 0, 0],
        rotation: [0, 0, 0],
        axis: 'z',
        targetFeatureId: sh_id,
        catalogSource: `METRIC_SCREWS.${d.holeSize}.recommendedCounterboreDepth`,
        callout: { headStyle: 'socket', nominalSize: d.holeSize },
        paramRefs: [],
      });
    } else if (d.holeFinish === 'countersunk' && screwSpec) {
      out.push({
        id: `cs-${i + 1}`,
        partId: platePartId,
        kind: 'countersink',
        diameter: screwSpec.countersunkHeadDiameter,
        angle: 90,
        position: [x, 0, 0],
        rotation: [0, 0, 0],
        axis: 'z',
        targetFeatureId: sh_id,
        catalogSource: `METRIC_SCREWS.${d.holeSize}.countersunkHeadDiameter`,
        callout: { headStyle: 'flat', nominalSize: d.holeSize, through: true },
        paramRefs: [],
      });
    }
  }

  if (intake.qualityLevel === 'extreme') {
    out.push({
      id: 'edge_chamfer',
      partId: platePartId,
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 0.5,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }
  return out;
}
